/**
 * SalesOpsToggle — the canonical "Sales | Ops" segmented view switcher.
 *
 * One presentational control, used consistently across every surface that
 * has a sales lens and an ops lens: the home dashboard, Jobs, and
 * Estimates. Each surface wires `onSelect` to its own navigation model
 * (a route change, a `?view=` param, or the home-persona preference) — this
 * component owns only the look and the tab semantics, so the switcher reads
 * identically everywhere.
 */

export type SalesOpsView = "sales" | "ops";

const TABS: { id: SalesOpsView; label: string }[] = [
  { id: "sales", label: "Sales" },
  { id: "ops", label: "Ops" },
];

export function SalesOpsToggle({
  current,
  onSelect,
  ariaLabel = "View",
}: {
  current: SalesOpsView;
  onSelect: (view: SalesOpsView) => void;
  ariaLabel?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="inline-flex rounded-md border border-border overflow-hidden"
    >
      {TABS.map((tab, i) => {
        const active = current === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => {
              if (!active) onSelect(tab.id);
            }}
            className={`px-3 py-1.5 text-sm transition-colors ${
              i > 0 ? "border-l border-border" : ""
            } ${
              active
                ? "bg-primary text-white"
                : "bg-surface text-foreground hover:bg-surface-hover"
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
